import { useEffect, useMemo, useState } from "react";
import {
  listUnanswered,
  resolveUnanswered,
  deleteUnanswered,
  type UnansweredQuestion,
} from "../api/unanswered";
import Icon from "../components/icons";

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return s;
  }
}

// Mevzuat Analizi'nde kullanıcıların sorduğu ama yüklü dokümanlarda cevabı
// bulunamayan sorular. Admin bu listeye göre corpus'a uygun doküman ekler.
/** Reddin türü. Bu liste "hangi doküman eksik" sorusunu yanıtlamak için var,
 *  ama iki bambaşka durum aynı kutuda görünüyordu:
 *
 *    chunks_used = 0  → arama hiç parça bulamadı. Kesin: corpus boşluğu,
 *                       çözümü doküman yüklemek.
 *    chunks_used > 0  → parça geldi ama yetmedi. SEBEBİ TEK DEĞİL: parçalar
 *                       ilgisiz olabilir (arama/corpus) ya da cevap içlerinde
 *                       olup model görmemiş olabilir (prompt/model).
 *
 *  Ölçüldü 2026-08-16/17 (üretim): iki kayıtta da 8 parça bulunmuştu. Biri
 *  92 sn sonra aynı modelle düzgün cevaplandı (örnekleme rastgeleliği), diğeri
 *  temperature=0 ile de reddedildi ve modelin kendi gerekçesi "getirilen
 *  parçalar sertifika geçerlilik süresi kuralını içermiyor" dedi — yani orada
 *  sorun modelde değil, doğru maddenin getirilmemesinde. Bu yüzden etiket
 *  sebebe değil ÖLÇÜLEBİLENE bakar: kaynak var mı, yetti mi.
 *
 *  Renk tek başına durum taşımaz (design-system §2) — rozet metni de yazar. */
function redTuru(n?: number | null): { etiket: string; sinif: string; aciklama: string } {
  if (n === undefined || n === null) {
    return {
      etiket: "—",
      sinif: "neutral",
      aciklama: "Bu kayıt, kaynak sayısı ölçümü eklenmeden önce yazılmış — tür bilinmiyor.",
    };
  }
  if (n === 0) {
    return {
      etiket: "Kaynak yok",
      sinif: "warn",
      aciklama: "Arama hiç kaynak parçası bulamadı — corpus boşluğu. Çözüm: ilgili dokümanı data/ altına ekleyip yükle.",
    };
  }
  return {
    etiket: "Kaynak yetersiz",
    sinif: "off",
    aciklama:
      `Arama ${n} parça getirdi ama model “yeterli bilgi yok” dedi. İki sebebi olabilir: ` +
      `getirilen parçalar konuyla ilgili değildir (arama/corpus işi), ya da cevap ` +
      `parçaların içindedir ve model göremedi (prompt/model işi). Ayırmak için soruyu ` +
      `panelde tekrar sorup dönen kaynak başlıklarına bakın.`,
  };
}

export default function UnansweredQuestions() {
  const [rows, setRows] = useState<UnansweredQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [q, setQ] = useState("");

  // Soru metni ve firma adında ara. Liste büyüdükçe "hangi boşluk en çok
  // soruluyor" sorusuna gözle bakmak zorlaşıyor.
  const trLower = (t: string) => t.toLocaleLowerCase("tr-TR");
  const filtered = useMemo(() => {
    const needle = trLower(q.trim());
    if (!needle) return rows;
    return rows.filter(
      (r) => trLower(r.question).includes(needle) || trLower(r.org_name || "").includes(needle),
    );
  }, [rows, q]);

  async function reload() {
    setLoading(true);
    setErr(null);
    try {
      setRows(await listUnanswered(openOnly));
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOnly]);

  const totalAsked = useMemo(() => rows.reduce((n, r) => n + r.times_asked, 0), [rows]);
  // Corpus boşluğu = aramanın HİÇ parça bulamadığı satırlar. Ayrı sayılır çünkü
  // tek kesin teşhis bu: doküman eksik, yükle. "Kaynak yetersiz" satırları
  // sayıya girmez — sebebi tek değil, önce tek tek bakılması gerekir.
  const corpusBosluk = useMemo(() => rows.filter((r) => r.chunks_used === 0).length, [rows]);

  async function onResolve(r: UnansweredQuestion) {
    setBusyId(r.id);
    setErr(null);
    try {
      await resolveUnanswered(r.id);
      await reload();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "İşaretlenemedi");
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(r: UnansweredQuestion) {
    if (!window.confirm("Bu kayıt kalıcı olarak silinsin mi?")) return;
    setBusyId(r.id);
    setErr(null);
    try {
      await deleteUnanswered(r.id);
      await reload();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Silinemedi");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <div className="toolbar">
        <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
          Yalnızca açık (çözülmemiş)
        </label>
        <button className="btn-primary" onClick={() => void reload()} disabled={loading}>
          Yenile
        </button>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="data-table-card">
        <div className="data-table-toolbar">
          <label className="data-search">
            <Icon name="search" size={15} />
            <input
              type="search"
              placeholder="Soru veya firma ara…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Cevapsız sorularda ara"
            />
          </label>
          <span className="data-count">
            {q.trim() ? `${filtered.length}/${rows.length}` : rows.length} soru · {totalAsked} kez soruldu
            {corpusBosluk > 0 && <> · <strong>{corpusBosluk}</strong> corpus boşluğu</>}
          </span>
        </div>
        {loading ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <Icon name="inbox" size={34} />
            <p>{openOnly ? "Açık cevapsız soru yok." : "Hiç cevapsız soru kaydı yok."}</p>
          </div>
        ) : (
          // table-wrap: yapışkan başlık + uzun listede kendi içinde kaydırma
          // (ui-admin §4). Liste büyüdükçe sütun başlıkları kaybolmasın.
          <div className="table-wrap">
            <table className="table">
            <thead>
              <tr>
                {/* "#" değil "ID": satır numarası değil kayıt kimliği. Liste
                    tarihe göre sıralı olduğu için numaralar ardışık DEĞİL;
                    "#" başlığı bunu sıralama hatası gibi gösteriyordu. */}
                <th className="num">ID</th>
                <th>Firma</th>
                <th className="num">Kez</th>
                <th className="nowrap">Son soruluş</th>
                <th>Soru</th>
                <th>Tür</th>
                <th>Durum</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="dt-empty">“{q}” ile eşleşen soru yok.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id}>
                  <td className="num">{r.id}</td>
                  <td>{r.org_name || (r.org_id ? `#${r.org_id}` : "—")}</td>
                  <td className="num"><span className={"badge " + (r.times_asked > 1 ? "warn" : "neutral")}>{r.times_asked}×</span></td>
                  <td className="nowrap">{fmtDate(r.last_asked)}</td>
                  <td className="clamp-cell">
                    {/* Tam metin title'da — kırpılan soru bir hover uzakta. */}
                    <div className="clamp" title={r.question}>{r.question}</div>
                  </td>
                  <td>
                    {(() => {
                      const t = redTuru(r.chunks_used);
                      return <span className={"badge " + t.sinif} title={t.aciklama}>{t.etiket}</span>;
                    })()}
                  </td>
                  <td>
                    {r.resolved_at
                      ? <span className="badge ok">Çözüldü</span>
                      : <span className="badge off">Açık</span>}
                  </td>
                  <td className="row-actions">
                    {!r.resolved_at && (
                      <button className="link" onClick={() => onResolve(r)} disabled={busyId === r.id}>
                        Çözüldü
                      </button>
                    )}
                    <button className="link" onClick={() => onDelete(r)} disabled={busyId === r.id}>
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </>
  );
}
